import { useEffect, useRef } from 'react';
import Konva from 'konva';
import { FieldTransform } from './FieldCanvas';

interface GridLayerProps {
  stage: Konva.Stage;
  transform: FieldTransform;
  visible: boolean;
  snapFt: number;
}

const MAJOR_FT = 10;

export const GridLayer: React.FC<GridLayerProps> = ({ stage, transform, visible, snapFt }) => {
  const layerRef = useRef<Konva.Layer | null>(null);

  useEffect(() => {
    const layer = new Konva.Layer({ listening: false });
    layer.name('grid');
    stage.add(layer);
    layer.setZIndex(1);
    layerRef.current = layer;
    return () => { layer.destroy(); };
  }, [stage]);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.destroyChildren();

    if (visible) {
      const { basePxPerFoot: ppf, fieldOriginX: ox, fieldOriginY: oy,
              canvasHorizFt, canvasVertFt } = transform;
      const fw = canvasHorizFt * ppf;
      const fh = canvasVertFt  * ppf;

      const addLine = (points: number[], opacity: number) =>
        layer.add(new Konva.Line({
          points, stroke: '#ffffff', strokeWidth: 0.5, opacity, listening: false,
        }));

      // Minor grid — finer than 10ft, skip positions that fall on the major grid
      if (snapFt > 0 && snapFt < MAJOR_FT) {
        for (let x = snapFt; x < canvasHorizFt; x += snapFt) {
          if (x % MAJOR_FT === 0) continue;
          addLine([ox + x * ppf, oy, ox + x * ppf, oy + fh], 0.07);
        }
        for (let y = snapFt; y < canvasVertFt; y += snapFt) {
          if (y % MAJOR_FT === 0) continue;
          addLine([ox, oy + y * ppf, ox + fw, oy + y * ppf], 0.07);
        }
      }

      // Major 10ft grid
      for (let x = MAJOR_FT; x < canvasHorizFt; x += MAJOR_FT) {
        addLine([ox + x * ppf, oy, ox + x * ppf, oy + fh], 0.55);
      }
      for (let y = MAJOR_FT; y < canvasVertFt; y += MAJOR_FT) {
        addLine([ox, oy + y * ppf, ox + fw, oy + y * ppf], 0.55);
      }

      // Edge labels at every 10ft mark
      const labelStyle = { fontSize: 9, fill: '#ffffff', opacity: 0.75, listening: false };
      const lw = 28; // fixed label box width — wide enough for "150"

      for (let i = 1; i * MAJOR_FT < canvasHorizFt; i++) {
        const ft = i * MAJOR_FT;
        const cx = ox + ft * ppf;
        // Top and bottom
        layer.add(new Konva.Text({ ...labelStyle, x: cx - lw / 2, y: oy - 12, width: lw, text: String(ft), align: 'center' }));
        layer.add(new Konva.Text({ ...labelStyle, x: cx - lw / 2, y: oy + fh + 2, width: lw, text: String(ft), align: 'center' }));
      }

      for (let j = 1; j * MAJOR_FT < canvasVertFt; j++) {
        const ft = j * MAJOR_FT;
        const cy = oy + ft * ppf;
        // Left (right-aligned toward field edge) and right
        layer.add(new Konva.Text({ ...labelStyle, x: ox - lw - 2, y: cy - 5, width: lw, text: String(ft), align: 'right' }));
        layer.add(new Konva.Text({ ...labelStyle, x: ox + fw + 3,  y: cy - 5, width: lw, text: String(ft), align: 'left'  }));
      }
    }

    layer.batchDraw();
  }, [transform, visible, snapFt]);

  return null;
};
